import type React from 'react';
import { useEffect, useMemo, useState } from 'react';
import type { DispatchMetric } from '../../api/types';
import { PresetPicker } from './PresetPicker';
import { type Preset, selected, startingPicks } from './presets';
import { Field } from '../../components/Field';
import { Spinner } from '../../components/Spinner';
import { CATEGORIES, type MetricType } from './metricMeta';
import {
  type MetricForm,
  blankForm,
  disabledReason,
  formOf,
  headerPresets,
  isFeedForm,
  isSnapshotForm,
  offeredTypes,
  suggestedName,
  subcategoryOptions,
  validateForm,
  withCategory,
  withSubcategory,
  withType,
  writePayload,
} from './metricForm';

/**
 * Create or edit one dispatch metric.
 *
 * A port of the dashboard's `metric_form_dialog.dart` with ONE control
 * deliberately missing: the per-metric time frame. There the window belongs to
 * each card and the dialog is where you set it; here the window belongs to the
 * page, so a control for it would be editing something this screen cannot show
 * the effect of. `metricForm.ts` derives the pair instead, and its whole job is
 * that an edit made here leaves the Flutter page drawing what it drew before.
 *
 * The rest is the dashboard's rules, because the row is the same row: a KPI
 * draws exactly one metric, a chart draws several of ONE value kind, a snapshot
 * and a dispatch bucket can only be KPIs, and a KPI's header is stored in
 * `arrangeBy` where a chart keeps its arrangement.
 *
 * Creating also offers a READY-MADE tab beside the form — curated sets, for the
 * newcomer the blank form asks four unanswerable questions. Custom leads and is
 * the default, because building one is what this dialog has always been for and
 * the presets are the shortcut, not the replacement. EDITING shows no tabs at
 * all: there is one card in front of you and a list of sets is not about it.
 */

const TYPE_LABELS: Record<MetricType, string> = {
  barChart: 'Bar chart',
  lineChart: 'Line chart',
  kpi: 'KPI',
};

const TYPE_HINTS: Record<MetricType, string> = {
  barChart: 'Compares values over the page’s window as bars.',
  lineChart: 'Draws the same values as a trend line.',
  kpi: 'One figure, with the change against the previous window.',
};

const CUSTOM = '__custom__';

/**
 * `Field`, but a `<div role="group">` rather than a `<label>`.
 *
 * A `<label>` may wrap exactly ONE control. The type picker is three buttons
 * and the metric list is a stack of its own labels, so wrapping either in
 * `Field` nests labels and hands the controls the group's wording as their
 * accessible name — which is how a screen reader ends up announcing three
 * buttons all called "Show as", and why a role query cannot find any of them.
 */
function FieldGroup({
  label,
  hint,
  error,
  children,
}: {
  label: string;
  hint?: string;
  error?: string | null;
  children: React.ReactNode;
}) {
  return (
    <div className="field" role="group" aria-label={label}>
      <span className="field-label">{label}</span>
      {children}
      {error ? (
        <span className="field-error">{error}</span>
      ) : hint ? (
        <span className="field-hint">{hint}</span>
      ) : null}
    </div>
  );
}

export function MetricDialog({
  metric,
  metrics,
  busy = false,
  error,
  progress,
  onSave,
  onAddPresets,
  onCancel,
  onDelete,
}: {
  /** The row being edited, or null to create. */
  metric: DispatchMetric | null;
  /** What the dashboard already carries — the ready-made tab greys those out. */
  metrics: DispatchMetric[];
  busy?: boolean;
  error?: string | null;
  /** How far an add has got, so a two-dozen-card run is not a silent spinner. */
  progress?: { done: number; total: number } | null;
  onSave: (write: ReturnType<typeof writePayload>) => void;
  onAddPresets: (chosen: Preset[]) => void;
  onCancel: () => void;
  onDelete?: () => void;
}) {
  const [form, setForm] = useState<MetricForm>(() => (metric ? formOf(metric) : blankForm()));
  const [customHeader, setCustomHeader] = useState(false);
  const [touched, setTouched] = useState(false);
  /** Custom leads: building one is what this dialog is for, presets are the shortcut. */
  const [tab, setTab] = useState<'custom' | 'ready'>('custom');
  const [picked, setPicked] = useState<Set<string>>(() => startingPicks(metrics));

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !busy) onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onCancel]);

  const types = offeredTypes(form);
  const options = useMemo(() => subcategoryOptions(form.category), [form.category]);
  const refusal = validateForm(form);
  const presets = headerPresets(form.header);

  const chosen = selected(picked);
  const onReady = !metric && tab === 'ready';

  const submit = () => {
    if (onReady) {
      onAddPresets(chosen);
      return;
    }
    setTouched(true);
    if (refusal) return;
    onSave(writePayload(form, metric));
  };

  /**
   * Why the type is fixed, when it is. Said out loud rather than leaving a
   * control that silently does nothing: an operator who cannot choose deserves
   * to know it is the metric, not the screen.
   */
  const typeNote = isFeedForm(form)
    ? 'Dispatch metrics are counted from the job board, which answers a figure for right now — so they are KPIs.'
    : isSnapshotForm(form)
      ? 'This is an all-time figure with no period, so it can only be a KPI.'
      : TYPE_HINTS[form.type];

  return (
    <div
      className="dialog-backdrop"
      onMouseDown={(event) => event.target === event.currentTarget && !busy && onCancel()}
    >
      <div className="dialog db-dialog" role="dialog" aria-modal="true" aria-label={metric ? 'Edit metric' : 'New metric'}>
        <h2>{metric ? 'Edit metric' : 'New metric'}</h2>

        {/* Only when creating: editing has one card in front of you, and a list
            of ready-made sets is not about it. */}
        {!metric ? (
          <div className="db-tabs" role="tablist" aria-label="New metric">
            <button
              type="button"
              role="tab"
              aria-selected={tab === 'custom'}
              className={tab === 'custom' ? 'db-tab is-on' : 'db-tab'}
              disabled={busy}
              onClick={() => setTab('custom')}
            >
              Custom
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={tab === 'ready'}
              className={tab === 'ready' ? 'db-tab is-on' : 'db-tab'}
              disabled={busy}
              onClick={() => setTab('ready')}
            >
              Ready-made
            </button>
          </div>
        ) : null}

        <div className="dialog-body db-dialog-body">
          {onReady ? (
            <PresetPicker metrics={metrics} picked={picked} busy={busy} onPick={setPicked} />
          ) : (
          <>
          <Field label="Category">
            <select
              className="select"
              value={form.category}
              disabled={busy}
              onChange={(event) => setForm((current) => withCategory(current, event.target.value))}
            >
              {CATEGORIES.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.label}
                </option>
              ))}
            </select>
          </Field>

          <FieldGroup label="Show as" hint={typeNote}>
            <div className="db-typepick">
              {types.map((type) => (
                <button
                  key={type}
                  type="button"
                  className={form.type === type ? 'db-typebtn on' : 'db-typebtn'}
                  disabled={busy}
                  onClick={() => setForm((current) => withType(current, type))}
                >
                  {TYPE_LABELS[type]}
                </button>
              ))}
            </div>
          </FieldGroup>

          <FieldGroup
            label={form.type === 'kpi' ? 'Metric' : 'Metrics'}
            hint={
              form.type === 'kpi'
                ? 'A KPI draws exactly one.'
                : 'Several, as long as they are all counts or all amounts — one axis cannot be both.'
            }
            error={touched ? refusal : null}
          >
            <div className="mselect db-submenu">
              {options.length === 0 ? (
                <div className="mselect-empty">This category has no metrics.</div>
              ) : (
                options.map((option) => {
                  const picked = form.subCategory.includes(option.code);
                  // A picked option is never disabled — it has to stay
                  // unpickable-away-from, or a mixed row saved elsewhere could
                  // not be fixed from here.
                  const why = picked ? null : disabledReason(option.code, form);
                  return (
                    <label
                      key={option.code}
                      className={why ? 'mselect-row is-off' : 'mselect-row'}
                      title={why ?? undefined}
                    >
                      <input
                        type={form.type === 'kpi' ? 'radio' : 'checkbox'}
                        name={form.type === 'kpi' ? 'db-sub' : undefined}
                        checked={picked}
                        disabled={busy || !!why}
                        onChange={(event) =>
                          setForm((current) => withSubcategory(current, option.code, event.target.checked))
                        }
                      />
                      <span>
                        <span className="mselect-name">{option.label}</span>
                        {why ? <span className="mselect-sub"> · {why}</span> : null}
                      </span>
                    </label>
                  );
                })
              )}
            </div>
          </FieldGroup>

          <Field label="Name" hint="Shown on the card, here and on the dispatch dashboard.">
            <input
              className="input"
              value={form.metricsName}
              placeholder={suggestedName(form)}
              disabled={busy}
              onChange={(event) => setForm((current) => ({ ...current, metricsName: event.target.value }))}
            />
          </Field>

          {form.type === 'kpi' ? (
            <Field
              label="Header"
              hint="KPI cards sharing a header are grouped into a row. “Below Charts” puts them under the charts."
            >
              <select
                className="select"
                value={customHeader ? CUSTOM : form.header}
                disabled={busy}
                onChange={(event) => {
                  const value = event.target.value;
                  if (value === CUSTOM) {
                    setCustomHeader(true);
                    setForm((current) => ({ ...current, header: '' }));
                    return;
                  }
                  setCustomHeader(false);
                  setForm((current) => ({ ...current, header: value }));
                }}
              >
                <option value="">No header</option>
                {presets.map((preset) => (
                  <option key={preset} value={preset}>
                    {preset}
                  </option>
                ))}
                <option value={CUSTOM}>Custom…</option>
              </select>
            </Field>
          ) : null}

          {customHeader && form.type === 'kpi' ? (
            <input
              className="input"
              value={form.header}
              placeholder="Header name"
              disabled={busy}
              onChange={(event) => setForm((current) => ({ ...current, header: event.target.value }))}
            />
          ) : null}

          </>
          )}

          {/* The server's refusal, which is dispatch's own words and outranks
              anything this form worked out for itself — and it belongs to both
              tabs, since a half-finished add has the same claim on the space. */}
          {error ? <p className="field-error">{error}</p> : null}
        </div>

        <div className="dialog-actions db-dialog-actions">
          {metric && onDelete ? (
            <button type="button" className="btn btn-danger db-dialog-delete" onClick={onDelete} disabled={busy}>
              Delete
            </button>
          ) : null}
          <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={submit}
            disabled={busy || (onReady ? !chosen.length : touched && !!refusal)}
          >
            {busy && <Spinner />}
            {onReady
              ? busy && progress
                ? `Adding ${progress.done} of ${progress.total}…`
                : chosen.length
                  ? `Add ${chosen.length}`
                  : 'Add'
              : metric
                ? 'Save'
                : 'Create'}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Re-exported so the caller does not need the form module to type its handler. */
export type MetricWritePayload = ReturnType<typeof writePayload>;
